import { useEffect, useMemo, useState } from 'react';
import { useLazyGetFeatureInfoQuery } from '../api/weather';
import { useTranslation } from 'react-i18next';
import { Bbox } from 'ais-facade-api/api';
import { TargetPositionData } from 'ais-facade-api/api';
import { formatWindDirection } from '../helpers/formatWindDirection';

interface TempDataI {
  jiffle: number;
}
interface VectorSpeedDataI {
  uComponentWind: number;
  vComponentWind: number;
}

export const useGetWeatherData = (position: TargetPositionData | undefined) => {
  const { t } = useTranslation();
  const [tempData, setTempData] = useState<TempDataI>();
  const [vectorSpeedData, setVectorSpeedData] = useState<VectorSpeedDataI>();
  const [getFeature] = useLazyGetFeatureInfoQuery();

  const weatherLayers = JSON.parse(window._env_?.WEATHER_LAYERS);
  const bbox: Bbox | undefined = useMemo(() => {
    if (position?.latitude !== undefined && position?.longitude !== undefined) {
      const delta = 0.01;
      return {
        first: {
          latitude: position?.longitude - delta,
          longitude: position?.latitude - delta,
        },
        second: {
          latitude: position?.longitude + delta,
          longitude: position?.latitude + delta,
        },
        z: 0, // не нужный параметр
      };
    }
  }, [position]);

  useEffect(() => {
    const fetchWeatherData = () => {
      if (bbox) {
        getFeature({
          bbox: bbox,
          layers: `${weatherLayers.temperature}`,
        }).then((response) => {
          if (response?.data?.features[0]?.properties) {
            setTempData(response.data.features[0].properties);
          }
        });

        getFeature({
          bbox: bbox,
          layers: `${weatherLayers.wind}`,
        }).then((response) => {
          if (response?.data?.features[0]?.properties) {
            setVectorSpeedData(response.data.features[0].properties);
          }
        });
      }
    };
    fetchWeatherData();
  }, [bbox, getFeature]);

  /**
   * Рассчитывает направление и скорость ветра на основе компонентов вектора ветра.
   * @param {VectorSpeedDataI} windVector Объект, содержащий компоненты скорости ветра (u и v).
   * @returns {object} Объект, содержащий направление и скорость ветра.
   */
  const calculateWindDirectionAndSpeed = (windVector: VectorSpeedDataI) => {
    const [u, v] = Object.values(windVector);
    const windDirectionRadians = Math.atan2(v, u); // Вычисление угла направления ветра в радианах
    const windDirectionDegrees = (windDirectionRadians * 180) / Math.PI; // Преобразование радиан в градусы
    const windDirection = formatWindDirection(Math.round((windDirectionDegrees + 360) % 360)); // перевод значения с -180-180 в диапозон от 0-360°.
    const windSpeed = `${Math.round(Math.sqrt(u ** 2 + v ** 2))}${'\u0020'}${t('WindSpeedUnit')}`; // Вычисление скорости ветра
    return { windDirection, windSpeed };
  };

  const windData = vectorSpeedData ? calculateWindDirectionAndSpeed(vectorSpeedData) : undefined;
  const airTemp = tempData && 'jiffle' in tempData ? `${Math.round(tempData.jiffle)}\u00B0C` : undefined;

  if (windData && windData.windDirection !== undefined) {
    return { ...windData, airTemp };
  }
};
